import { useFeedback } from "@taunggyi-plus/auth";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import { useAuth } from "@taunggyi-plus/auth";
import type { Locale } from "@taunggyi-plus/domain";

import {
  toNotificationSummary,
  urlBase64ToUint8Array,
  type NotificationRow,
  type NotificationSummary,
} from "./core";

const labels = {
  en: {
    seeMore: "See more",
    back: "Back to notifications",
    title: "Notifications",
    empty: "You’re all caught up.",
    close: "Close notifications",
    markAll: "Mark all as read",
    acknowledge: "Acknowledge",
    unread: "unread",
    signIn: "Sign in to see notifications",
    pushOn: "Device alerts on",
    pushOff: "Enable device alerts",
    pushDisable: "Disable device alerts",
    pushUnsupported: "Device alerts are unavailable in this browser.",
    pushDenied: "Notification permission was denied in browser settings.",
  },
  my: {
    seeMore: "ထပ်ကြည့်မည်",
    back: "အသိပေးချက်များသို့ ပြန်သွားမည်",
    title: "အသိပေးချက်များ",
    empty: "အသိပေးချက်အသစ် မရှိပါ။",
    close: "အသိပေးချက်များ ပိတ်မည်",
    markAll: "အားလုံး ဖတ်ပြီးဟု သတ်မှတ်မည်",
    acknowledge: "လက်ခံသိရှိသည်",
    unread: "မဖတ်ရသေး",
    signIn: "အသိပေးချက်များကြည့်ရန် အကောင့်ဝင်ပါ",
    pushOn: "စက် အသိပေးချက် ဖွင့်ထားသည်",
    pushOff: "စက် အသိပေးချက် ဖွင့်မည်",
    pushDisable: "စက် အသိပေးချက် ပိတ်မည်",
    pushUnsupported: "ဤဘရောက်ဇာတွင် စက်အသိပေးချက် မရနိုင်ပါ။",
    pushDenied: "ဘရောက်ဇာဆက်တင်တွင် အသိပေးချက်ကို ပိတ်ထားသည်။",
  },
  shn: {
    seeMore: "တူၺ်းထႅင်ႈ",
    back: "ၶိုၼ်းၵႂႃႇၶေႃႈပွင်ႇၶၢဝ်ႇ",
    title: "ၶေႃႈပွင်ႇၶၢဝ်ႇ",
    empty: "ဢမ်ႇမီးၶေႃႈပွင်ႇၶၢဝ်ႇမႂ်ႇ။",
    close: "ပိၵ်ႉၶေႃႈပွင်ႇၶၢဝ်ႇ",
    markAll: "မၢႆဝႃႈဢၢၼ်ႇယဝ်ႉတင်းမူတ်း",
    acknowledge: "ႁပ်ႉႁူႉ",
    unread: "ပႆႇဢၢၼ်ႇ",
    signIn: "ၶဝ်ႈဢၶွင်ႉတႃႇတူၺ်းၶေႃႈပွင်ႇၶၢဝ်ႇ",
    pushOn: "ပိုတ်ႇၶၢဝ်ႇၼိူဝ်ၶိူင်ႈဝႆႉ",
    pushOff: "ပိုတ်ႇၶၢဝ်ႇၼိူဝ်ၶိူင်ႈ",
    pushDisable: "ပိၵ်ႉၶၢဝ်ႇၼိူဝ်ၶိူင်ႈ",
    pushUnsupported: "Browser ၼႆႉဢမ်ႇႁပ်ႉၶၢဝ်ႇၼိူဝ်ၶိူင်ႈ။",
    pushDenied: "ပိၵ်ႉၶၢဝ်ႇဝႆႉၼႂ်း browser settings။",
  },
  zh: {
    seeMore: "查看更多",
    back: "返回通知",
    title: "通知",
    empty: "您已查看所有通知。",
    close: "关闭通知",
    markAll: "全部标为已读",
    acknowledge: "确认收到",
    unread: "未读",
    signIn: "登录后查看通知",
    pushOn: "设备通知已开启",
    pushOff: "开启设备通知",
    pushDisable: "关闭设备通知",
    pushUnsupported: "此浏览器不支持设备通知。",
    pushDenied: "浏览器设置已拒绝通知权限。",
  },
} as const;

type NotificationAppId =
  "customer" | "admin" | "hotel" | "merchant" | "delivery-owner" | "delivery-rider" | "taxi";

export function NotificationCenter({
  locale,
  appId,
  webPushPublicKey,
  embedded = false,
}: {
  locale: Locale;
  appId: NotificationAppId;
  webPushPublicKey?: string;
  embedded?: boolean;
}) {
  const auth = useAuth();
  const panelId = useId();
  const [recordUrl, setRecordUrl] = useState<string | null>(null);
  const [items, setItems] = useState<NotificationSummary[]>([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const notificationId = new URLSearchParams(window.location.search).get("notification");
  const [record, setRecord] = useState<Record<string, unknown> | null>(null);
  const [notice, setNotice] = useState<NotificationSummary | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useFeedback("", "error");
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const text = labels[locale];
  const load = useCallback(async () => {
    if (!auth.client || !auth.user) return;
    const { data, error: queryError } = await auth.client.rpc("notification_page", { page });
    setLoading(false);
    if (queryError) setError(queryError.message);
    else {
      setError("");
      const result = data as { items: NotificationRow[]; total: number; unread: number };
      const rows = result.items.map(toNotificationSummary);
      setItems((previous) =>
        page === 0 ? rows : [...previous.filter((i) => !rows.some((r) => r.id === i.id)), ...rows],
      );
      setTotal(result.total);
      setCount(result.unread);
    }
  }, [auth.client, auth.user, page, setError]);

  useEffect(() => {
    const id = notificationId;
    if (!embedded || !id || !auth.client || !auth.user) return;
    let active = true;
    void auth.client
      .rpc("notification_destination", { notification_id: id, app_id: appId })
      .then(({ data, error }) => {
        if (!active) return;
        if (error) {
          setError(error.message);
          return;
        }
        const result = data as { url: string | null; notification: NotificationRow };
        window.dispatchEvent(new Event("taunggyi:notifications-changed"));
        if (result.url && !result.notification.requires_acknowledgement)
          window.location.replace(result.url);
        else {
          setRecordUrl(result.url);
          setNotice(toNotificationSummary(result.notification));
          void auth
            .client!.rpc("notification_record", { notification_id: id })
            .then(({ data, error }) => {
              if (!active) return;
              if (error) setError(error.message);
              else setRecord(data as Record<string, unknown> | null);
            });
        }
      });
    return () => {
      active = false;
    };
  }, [appId, auth.client, auth.user, embedded, notificationId, setError]);

  async function markAll() {
    if (!auth.client) return;
    const { error } = await auth.client.rpc("mark_all_notifications_read");
    if (error) setError(error.message);
    else {
      setItems((previous) => previous.map((i) => ({ ...i, readAt: new Date().toISOString() })));
      window.dispatchEvent(new Event("taunggyi:notifications-changed"));
    }
  }

  useEffect(() => {
    if (!auth.client || !auth.user) {
      return;
    }
    const initialLoad = window.setTimeout(() => void load(), 0);
    const timer = window.setInterval(() => void load(), 30_000);
    const changed = () => void load();
    window.addEventListener("taunggyi:notifications-changed", changed);
    return () => {
      window.clearTimeout(initialLoad);
      window.clearInterval(timer);
      window.removeEventListener("taunggyi:notifications-changed", changed);
    };
  }, [auth.client, auth.user, load]);

  useEffect(() => {
    if (!auth.user || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      return;
    }
    let active = true;
    void navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then((subscription) => {
        if (active) setPushEnabled(Boolean(subscription));
      })
      .catch(() => {
        if (active) setPushEnabled(false);
      });
    return () => {
      active = false;
    };
  }, [auth.user]);

  useEffect(() => {
    if (!open || embedded) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [embedded, open]);

  async function update(ids: string[], values: { read_at?: string; acknowledged_at?: string }) {
    if (!auth.client || !ids.length) return;
    const { error: updateError } = await auth.client
      .from("notifications")
      .update(values)
      .in("id", ids);
    if (updateError) setError(updateError.message);
    else {
      if (values.acknowledged_at)
        setNotice((previous) =>
          previous ? { ...previous, requiresAcknowledgement: false } : previous,
        );
      window.dispatchEvent(new Event("taunggyi:notifications-changed"));
    }
  }

  function openCenter() {
    setOpen((value) => !value);
  }

  async function togglePush() {
    if (!auth.client || !auth.user) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setError(text.pushUnsupported);
      return;
    }
    setPushBusy(true);
    setError("");
    try {
      const registration = await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      if (current) {
        const endpoint = current.endpoint;
        await current.unsubscribe();
        const { error: revokeError } = await auth.client
          .from("push_subscriptions")
          .update({ revoked_at: new Date().toISOString() })
          .eq("endpoint", endpoint)
          .eq("app_id", appId);
        if (revokeError) throw revokeError;
        setPushEnabled(false);
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setError(text.pushDenied);
        return;
      }
      if (!webPushPublicKey) throw new Error(text.pushUnsupported);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(webPushPublicKey),
      });
      const keys = subscription.toJSON().keys;
      if (!keys?.p256dh || !keys.auth) throw new Error(text.pushUnsupported);
      const existing = await auth.client
        .from("push_subscriptions")
        .select("id")
        .eq("endpoint", subscription.endpoint)
        .eq("app_id", appId)
        .maybeSingle();
      const payload = {
        user_id: auth.user.id,
        app_id: appId,
        endpoint: subscription.endpoint,
        p256dh_key: keys.p256dh,
        auth_key: keys.auth,
        user_agent: navigator.userAgent.slice(0, 500),
        revoked_at: null,
      };
      const result = existing.data
        ? await auth.client.from("push_subscriptions").update(payload).eq("id", existing.data.id)
        : await auth.client.from("push_subscriptions").insert(payload);
      if (result.error) {
        await subscription.unsubscribe();
        throw result.error;
      }
      setPushEnabled(true);
    } catch (pushError) {
      setError(pushError instanceof Error ? pushError.message : text.pushUnsupported);
    } finally {
      setPushBusy(false);
    }
  }

  return (
    <div
      className={`notification-center ${embedded ? "notification-center--embedded" : ""}`}
      ref={panelRef}
    >
      {!embedded && (
        <button
          className="notification-button"
          type="button"
          aria-label={auth.user ? text.title : text.signIn}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={openCenter}
        >
          <svg className="header-action-glyph" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18 9a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4" />
          </svg>
          {count > 0 && (
            <span className="notification-button__badge">{count > 99 ? "99+" : count}</span>
          )}
        </button>
      )}
      {(open || embedded) && (
        <div
          className={`notification-panel ${embedded ? "notification-panel--embedded" : ""}`}
          id={panelId}
          role={embedded ? "region" : "dialog"}
          aria-label={text.title}
        >
          <div className="notification-panel__header">
            <div>
              {!embedded && <h2>{text.title}</h2>}
              <small>
                {count} {text.unread}
              </small>
            </div>
            <div className="notification-header-actions">
              {auth.user && (
                <button className="text-button" disabled={!count} onClick={() => void markAll()}>
                  {text.markAll}
                </button>
              )}
              {!embedded && (
                <button
                  className="icon-button"
                  type="button"
                  aria-label={text.close}
                  onClick={() => setOpen(false)}
                >
                  ×
                </button>
              )}
            </div>
          </div>
          {auth.user && (
            <button
              className="notification-push-toggle"
              disabled={pushBusy}
              onClick={() => void togglePush()}
            >
              {pushBusy ? "…" : pushEnabled ? text.pushDisable : text.pushOff}
            </button>
          )}
          {!auth.user ? (
            <p className="notification-empty">{text.signIn}</p>
          ) : notice ? (
            <article className="notification-detail-page">
              <a
                href="/notifications"
                onClick={() => {
                  setNotice(null);
                  setRecord(null);
                }}
              >
                ← {text.back}
              </a>
              {recordUrl && (
                <a className="button button--secondary" href={recordUrl}>
                  View record
                </a>
              )}
              <h2>{notice.title}</h2>
              <p>{notice.body}</p>
              {record && (
                <dl>
                  {Object.entries(record).map(([label, value]) => (
                    <div key={label}>
                      <dt>{label.replace(" Minor", " (MMK)")}</dt>
                      <dd>
                        {label.endsWith(" Minor")
                          ? (Number(value) / 100).toLocaleString()
                          : label === "Created At"
                            ? new Date(String(value)).toLocaleString()
                            : String(value).replaceAll("_", " ")}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
              <time dateTime={notice.createdAt}>
                {new Date(notice.createdAt).toLocaleString(locale === "shn" ? "en" : locale)}
              </time>
              {notice.requiresAcknowledgement && (
                <button
                  className="text-button"
                  onClick={() =>
                    void update([notice.id], {
                      acknowledged_at: new Date().toISOString(),
                      read_at: new Date().toISOString(),
                    })
                  }
                >
                  {text.acknowledge}
                </button>
              )}
            </article>
          ) : loading ? (
            <p className="notification-empty" role="status">
              …
            </p>
          ) : items.length === 0 ? (
            <p className="notification-empty">{text.empty}</p>
          ) : (
            <ul className="notification-simple-list">
              {(embedded ? items : items.slice(0, 5)).map((item) => (
                <li key={item.id}>
                  <a
                    className={`notification-row ${item.readAt ? "" : "notification-row--unread"}`}
                    href={`/notifications?notification=${encodeURIComponent(item.id)}`}
                    onClick={() => setOpen(false)}
                  >
                    <span
                      className="notification-read-dot"
                      aria-label={item.readAt ? undefined : text.unread}
                    />
                    <span>
                      <strong>{item.title}</strong>
                      <time dateTime={item.createdAt}>
                        {new Intl.DateTimeFormat(locale === "shn" ? "en" : locale, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        }).format(new Date(item.createdAt))}
                      </time>
                    </span>
                    <span aria-hidden="true">›</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
          {auth.user &&
            !notice &&
            (!embedded ? (
              <a
                className="notification-see-more"
                href="/notifications"
                onClick={() => setOpen(false)}
              >
                {text.seeMore}
              </a>
            ) : (
              items.length < total && (
                <button
                  className="notification-see-more"
                  onClick={() => setPage((value) => value + 1)}
                >
                  {text.seeMore}
                </button>
              )
            ))}
          {error && (
            <p className="notification-error" role="status">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
